if (!customElements.get('marcas-motos')) {
  const SPEED = 40; // px per second of the logo strip
  const reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)');

  // Motorcycle brands (sections/marcas-motos.liquid). The strip moves with a CSS animation
  // (assets/section-marcas-motos.css) inside a window that also scrolls by finger or trackpad. This element
  // repeats the logos so the strip never ends, and opens the stage, where the chosen brand's scene (one
  // <template> per brand) plays its entrance.
  customElements.define(
    'marcas-motos',
    class MarcasMotos extends HTMLElement {
      connectedCallback() {
        this.track = this.querySelector('.mm__trilho');
        this.scenes = this.querySelector('.mm__cenas');
        if (!this.track || !this.scenes) return;
        if (!this.initialized) this.init();
        this.resizeObserver.observe(this.viewport);
        this.intersectionObserver.observe(this);
        reduceMotion.addEventListener('change', this.fill);
      }

      disconnectedCallback() {
        this.resizeObserver?.disconnect();
        this.intersectionObserver?.disconnect();
        reduceMotion.removeEventListener('change', this.fill);
      }

      init() {
        this.initialized = true;
        this.viewport = this.querySelector('.mm__janela');
        this.stage = this.querySelector('.mm__palco');
        this.fill = this.fill.bind(this);
        this.resizeObserver = new ResizeObserver(this.fill);
        // The strip only moves near the screen.
        this.intersectionObserver = new IntersectionObserver(
          ([entry]) => this.classList.toggle('mm--fora', !entry.isIntersecting),
          { rootMargin: '100px 0px' }
        );

        this.track.addEventListener('click', (event) => this.choose(event.target.closest('.mm__marca')?.dataset.marca));
        // One tab stop for the strip, on the last logo used; the arrows, Home and End move between the logos.
        this.logos = [...this.track.querySelectorAll('button.mm__marca')];
        this.tabStop(this.logos[0]);
        this.track.addEventListener('keydown', (event) => this.onKey(event));
        this.track.addEventListener('focusin', (event) => this.tabStop(event.target.closest('.mm__marca')));
        // Keyboard on a logo: the strip stops moving and its offset goes into the scroll, so the browser can bring
        // any logo into view (it cannot scroll a transform back); it moves again once the focus leaves the strip.
        this.track.addEventListener('focusin', (event) => {
          if (this.classList.contains('mm--teclado') || !event.target.matches(':focus-visible')) return;
          const offset = new DOMMatrixReadOnly(getComputedStyle(this.track).transform).m41;
          this.classList.add('mm--teclado');
          this.scrollTo(this.viewport.scrollLeft - offset);
          event.target.scrollIntoView({ block: 'nearest', inline: 'nearest' });
        });
        this.viewport.addEventListener('focusout', (event) => {
          if (!this.viewport.contains(event.relatedTarget)) this.classList.remove('mm--teclado');
        });
        // Pointing at a logo (or tabbing to it) starts downloading its moto, so the click plays at once.
        const warm = (event) => this.warm(event.target.closest?.('.mm__marca')?.dataset.marca);
        this.track.addEventListener('pointerover', warm);
        this.track.addEventListener('focusin', warm);
        // A finger on the strip, or a scroll, holds it still until a moment after it ends.
        this.viewport.addEventListener('pointerdown', () => this.hold());
        this.viewport.addEventListener('scroll', () => this.onScroll(), { passive: true });
        this.querySelector('.mm__fechar').addEventListener('click', () => this.close());
        this.addEventListener('keydown', (event) => {
          if (event.key === 'Escape' && this.current) this.close();
        });
        // Theme editor: selecting a block shows its moto.
        this.addEventListener('shopify:block:select', (event) => {
          const id = event.target.querySelector?.('.mm__marca')?.dataset.marca;
          if (id && id !== this.current) this.choose(id);
        });
      }

      // Copies of the logos go after the originals (hidden from screen readers and the keyboard). The animation
      // moves the strip by one round of logos (--mm-distancia) and starts again: the copies are then where the
      // originals were, so nobody sees the jump. The window starts scrolled one round in, so the finger can
      // go both ways, and onScroll() keeps it there.
      fill() {
        this.track.querySelectorAll(':scope > [data-copia]').forEach((copy) => copy.remove());
        this.round = 0;
        const items = [...this.track.children];
        if (!items.length || reduceMotion.matches) return this.scrollTo(0);

        const copyAll = () =>
          items.forEach((item) => {
            const copy = item.cloneNode(true);
            copy.setAttribute('data-copia', '');
            copy.setAttribute('aria-hidden', 'true');
            copy.removeAttribute('data-shopify-editor-block');
            copy.querySelector('button')?.setAttribute('tabindex', '-1');
            this.track.append(copy);
          });
        copyAll();
        const round = this.track.children[items.length].offsetLeft - items[0].offsetLeft;
        if (round <= 0) return; // hidden (theme editor): measured again when it shows
        // Enough for: half a round of scroll on each side, plus one round of animation, plus the window.
        for (let i = 0; i < 30 && this.track.scrollWidth < this.viewport.clientWidth + round * 2.5; i++) copyAll();
        this.round = round;
        this.style.setProperty('--mm-distancia', `${round}px`);
        this.style.setProperty('--mm-tempo', `${(round / SPEED).toFixed(2)}s`);
        this.scrollTo(round);
      }

      scrollTo(left) {
        this.scrolling = left;
        this.viewport.scrollLeft = left;
      }

      hold() {
        this.classList.add('mm--mexendo');
        clearTimeout(this.holding);
        this.holding = setTimeout(() => this.classList.remove('mm--mexendo'), 1500);
      }

      onScroll() {
        const left = this.viewport.scrollLeft;
        if (this.scrolling !== undefined && Math.abs(left - this.scrolling) < 1) return; // our own scroll
        this.scrolling = undefined;
        this.hold();
        // Back by one round when the scroll gets near an end, except while the keyboard is on a logo (the
        // browser scrolled to show it).
        const focused = document.activeElement;
        if (!this.round || (this.viewport.contains(focused) && focused.matches(':focus-visible'))) return;
        if (left < this.round / 2) this.scrollTo(left + this.round);
        else if (left > this.round * 1.5) this.scrollTo(left - this.round);
      }

      tabStop(logo) {
        if (!this.logos.includes(logo)) return; // the copies are never a tab stop
        this.logos.forEach((button) => (button.tabIndex = button === logo ? 0 : -1));
      }

      onKey(event) {
        const index = this.logos.indexOf(event.target);
        const count = this.logos.length;
        if (index < 0) return;
        const next = { ArrowRight: index + 1, ArrowLeft: index - 1 + count, Home: 0, End: count - 1 }[event.key];
        if (next === undefined) return;
        event.preventDefault();
        this.logos[next % count].focus(); // the window scrolls to it
      }

      template(id) {
        return id ? this.querySelector(`template[data-cena="${CSS.escape(id)}"]`) : null;
      }

      warm(id) {
        const photo = this.template(id)?.content.querySelector('.mm__moto');
        if (!photo || photo.dataset.aquecida) return;
        photo.dataset.aquecida = 'true';
        new Image().src = photo.getAttribute('src');
      }

      // A click on the open brand closes the stage; on another brand, its moto replaces the current one.
      choose(id) {
        const template = this.template(id);
        if (!template) return;
        if (id === this.current) return this.close();

        const opening = !this.current;
        const reduce = reduceMotion.matches;
        this.current = id;
        clearTimeout(this.clearing);
        // The copies show the ring too.
        this.track
          .querySelectorAll('button.mm__marca')
          .forEach((button) => button.setAttribute('aria-expanded', String(button.dataset.marca === id)));
        this.classList.add('mm--aberta');

        // The moto on stage rides off forwards while the new one comes in.
        this.scenes.querySelectorAll(':scope > .mm__cena:not(.mm__cena--sai)').forEach((scene) => {
          scene.classList.add('mm__cena--sai');
          scene.setAttribute('aria-hidden', 'true');
          setTimeout(() => scene.remove(), 500);
        });

        const scene = document.importNode(template.content.firstElementChild, true);
        this.scenes.append(scene);
        // The moto starts just outside the stage, on the side it rides in from, so it shows from the first frame.
        const box = scene.querySelector('.mm__pista').getBoundingClientRect();
        const frame = this.scenes.getBoundingClientRect();
        const forwards = getComputedStyle(scene).getPropertyValue('--mm-sentido').trim() !== '-1';
        scene.style.setProperty('--mm-entrada', `${-Math.ceil(forwards ? box.right - frame.left : frame.right - box.left)}px`);
        // The entrance waits for the photo (at most 1.5s), so the moto never rides in as an empty box, and, on
        // opening, for the stage to be mostly open.
        const photo = scene.querySelector('.mm__moto');
        const wait = (ms) => new Promise((resolve) => setTimeout(resolve, ms));
        const loaded = Promise.race([photo.decode ? photo.decode().catch(() => {}) : null, wait(1500)]);
        // On a swap, a beat later than the one riding off, so the two never touch.
        const beat = reduce ? 0 : opening ? 160 : 150;
        Promise.all([loaded, wait(beat)]).then(() => scene.classList.add('mm__cena--pronta'));

        if (opening) {
          // The stage opens below the strip, maybe below the fold: bring all of it into view.
          setTimeout(
            () => this.stage.scrollIntoView({ block: 'nearest', behavior: reduce ? 'auto' : 'smooth' }),
            reduce ? 0 : 500
          );
        }
      }

      close() {
        if (!this.current) return;
        const logo = this.logos.find((button) => button.dataset.marca === this.current);
        this.current = null;
        this.classList.remove('mm--aberta');
        this.track.querySelectorAll('button.mm__marca').forEach((button) => button.setAttribute('aria-expanded', 'false'));
        // The focus was on the close button, which is hidden now: back to the brand's logo (the strip scrolls to it).
        if (this.stage.contains(document.activeElement)) logo?.focus();
        this.clearing = setTimeout(() => this.scenes.replaceChildren(), 400);
      }
    }
  );
}
